import { Button, DialogActions, DialogContent, Stack } from '@mui/material';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from './styles';
import { useAtomValue, useAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { selectedStructureId } from '@/state';
import { useCallback, useMemo, useState } from 'react';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { useCreateAssemblyMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PageLoader } from '@/components/shared/PageLoader';
import { MtComponent } from './MtComponent';
import { DwoComponent } from './DwoComponent';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_CREATE_ASSEMBLY_MUTATION_ERROR,
  STRUCTURE_ID_MISSING_ERROR,
} from '@/constants/Errors';

type Props = {
  onClose: () => void;
  open: boolean;
};

interface TemplateField {
  value: string;
  key: string;
}

export const NewComponentSelectionDialog = ({ open, onClose }: Props) => {
  const [assemblyTemplate, setAssemblyTemplate] = useAtom(state.assemblyTemplate);
  const isEsSheet = useAtomValue(state.hasRiskAssessment);
  const structureId = useAtomValue(selectedStructureId);
  if (!structureId) {
    ErrorLogger.log(STRUCTURE_ID_MISSING_ERROR.withCode('FABW-INS-0011'));
  }
  const [currentTemplateData, setCurrentTemplateData] = useState<TemplateField[]>([]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleClose = () => {
    setCurrentTemplateData([]);
    onClose();
  };

  const [createAssembly, { loading: createAssemblyLoading }] = useCreateAssemblyMutation({
    onCompleted: (response) => {
      if (response.createAssembly) {
        setAssemblyTemplate(response.createAssembly);
      }
      showSnackBar('The assembly has been created successfully', 'success');
      setCurrentTemplateData([]);
      handleClose();
    },
    onError: (error) => {
      showSnackBar(error.message, 'error');
      ErrorLogger.log(
        INSIGHTS_CREATE_ASSEMBLY_MUTATION_ERROR.withContext({
          structureId,
          tagName: currentTemplateData.find(
            (f) => f.key === (isEsSheet ? 'risk_equipment_tag' : 'equipment_tag')
          )?.value,
          currentTemplateData,
        }).withError(error)
      );
    },
  });

  const requiredMTKeys = useMemo(
    () =>
      new Set([
        'risk_equipment_tag',
        'risk_material_type',
        'risk_damage_class',
        'risk_component_type',
        'risk_level_2_component',
        'risk_level_1_component',
        'risk_damage_class',
        'risk_dock',
        'risk_structure',
      ]),
    []
  );

  const requiredDWOKeys = useMemo(
    () =>
      new Set([
        'recommendation_alarm_level',
        'corrosion_category',
        'circuit',
        'spec',
        'size',
        'service',
        'equipment_tag',
        'assembly_area',
        'nominal_corrosion_allowance',
      ]),
    []
  );

  const requiredFieldsFilled = useMemo(() => {
    const requiredKeys = isEsSheet ? requiredMTKeys : requiredDWOKeys;

    if (!assemblyTemplate) return;

    const existingRequiredFields = assemblyTemplate?.fields
      .filter((field) => field.key && requiredKeys.has(field.key))
      .map((field) => field.key);

    const filledRequiredFields = currentTemplateData.filter(
      (field) => existingRequiredFields.includes(field.key) && field.value
    ).length;

    return existingRequiredFields.length === filledRequiredFields;
  }, [currentTemplateData, isEsSheet, assemblyTemplate, requiredMTKeys, requiredDWOKeys]);

  const submitFields = useCallback(() => {
    const tagName = currentTemplateData.find(
      (field) => field.key === (isEsSheet ? 'risk_equipment_tag' : 'equipment_tag')
    )?.value;
    if (requiredFieldsFilled && structureId && tagName) {
      createAssembly({
        variables: {
          input: {
            structureId,
            tagName,
            templateFieldData: currentTemplateData,
          },
        },
      });
    }
  }, [createAssembly, currentTemplateData, isEsSheet, requiredFieldsFilled, structureId]);

  const onFieldValueChange = useCallback((value: string, fieldKey: string | undefined | null) => {
    if (!fieldKey) return;
    const result = { value, key: fieldKey };

    setCurrentTemplateData((current) => {
      const currentInfo = current || [];
      const index = currentInfo.findIndex((info) => info.key === fieldKey);

      if (index !== -1) {
        currentInfo[index] = result;
        return [...currentInfo];
      }

      return [...currentInfo, result];
    });
  }, []);

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="Create Component"
      crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
      sx={{ ...dialogOverlay, height: 'fit-content' }}
    >
      <DialogContent sx={{ maxHeight: '40vh' }}>
        <Stack gap={4} data-testid="new-component-selection-dialog-title">
          {createAssemblyLoading && (
            <Stack
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                zIndex: 1051,
              }}
            >
              <PageLoader hideProgress backdrop background="dark" />
            </Stack>
          )}
          {isEsSheet ? (
            <MtComponent onFieldValueChange={onFieldValueChange} requiredMTKeys={requiredMTKeys} />
          ) : (
            <DwoComponent
              onFieldValueChange={onFieldValueChange}
              requiredDWOKeys={requiredDWOKeys}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          id="cancel-create-component"
          onClick={handleClose}
          variant="text"
          sx={{ mr: 1, height: '36px' }}
        >
          Cancel
        </Button>
        <LoadingButton
          id="confirm-create-component"
          disabled={!requiredFieldsFilled}
          onClick={submitFields}
          loading={createAssemblyLoading}
        >
          Create Component
        </LoadingButton>
      </DialogActions>
    </GenericModal>
  );
};
